import _ from 'lodash';
import { PROPS_TO_DEHYDRATE, itemTypeIcons, translateKeys } from '@/trendData/baseItem.constants';
import { getItemColor } from '@/trendData/itemStoreColor.utilities';
import { ITEM_TYPES } from '@/trendData/trendData.constants';

export type Item = Record<string, any>;

// ---------------------------------------------------------------------------
// Pure read helpers — operate on a plain `items` array
// ---------------------------------------------------------------------------

/** Find the first item whose `id` matches, or `undefined`. */
export function findItem(items: Item[], id: string | number): Item | undefined {
  return _.find(items, ['id', id]);
}

/**
 * Moves each map entry from its swapped-out id to its swapped-in id, in place, mirroring the flux
 * stores renaming swapped items in place on TREND_SWAP_ITEMS. The runtime stores' swap actions
 * share this so their re-keying cannot drift apart.
 */
export function swapMapEntries(map: Record<string, unknown>, swaps: Record<string, string>): void {
  const entries = Object.entries(swaps)
    .filter(([source]) => Object.hasOwn(map, source))
    .map(([source, destination]) => ({ source, destination, value: map[source] }));
  entries.forEach(({ source }) => {
    delete map[source];
  });
  entries.forEach(({ destination, value }) => {
    map[destination] = value;
  });
}

/** Find the first item where `item[prop] === val`, or `undefined`. */
export function findItemByProp(items: Item[], prop: string, val: string | number): Item | undefined {
  return _.find(items, [prop, val]);
}

/** Index of the first item with the given id, or -1. */
export function findItemIndex(items: Item[], id: string): number {
  return _.findIndex(items, ['id', id]);
}

/**
 * All items that are children of `id` — either directly (`isChildOf`) or via
 * the secondary set (`otherChildrenOf`).
 */
export function findChildren(items: Item[], id: string | number): Item[] {
  if (!id) return [];
  return _.filter(items, (item) => item.isChildOf === id || _.includes(item.otherChildrenOf as string[], id));
}

/**
 * Returns true when the item should be written to the workstep. Items with a
 * `childType` (e.g. capsule-series) are managed by their parent and excluded.
 */
export function shouldDehydrateItem(item: Item): boolean {
  return _.isNil(item.childType);
}

/**
 * Builds a new item object with the fields shared by all trend items. Throws if
 * `itemType` is not a member of `ITEM_TYPES`.
 */
export function createItem(
  id: string,
  name: string | undefined,
  itemType: keyof typeof itemTypeIcons,
  props: Record<string, any>,
): Item {
  const validTypes = _.values(ITEM_TYPES);
  if (!_.includes(validTypes, itemType)) {
    throw new TypeError(`Not a valid itemType.${itemType}`);
  }
  return _.merge(
    {},
    {
      id,
      name,
      itemType,
      icon: itemTypeIcons[itemType],
      translateKey: translateKeys[itemType],
      selected: false,
      autoDisabled: false,
      isArchived: false,
      color: getItemColor(_.get(props, 'color')),
    },
    props,
  );
}

/**
 * Returns a dehydrated item pruned to the standard persistence properties.
 * When `axisAutoScale` is set, the explicit axis bounds are excluded (they are
 * derived from the signal range and would conflict on rehydrate).
 */
export function pruneDehydratedItemProps<T extends Record<string, unknown>>(
  item: T,
  extraPropsToDehydrate: string[] = [],
): T {
  const propsToDehydrate = [...PROPS_TO_DEHYDRATE, ...extraPropsToDehydrate] as const;
  if ((item as { axisAutoScale?: boolean }).axisAutoScale) {
    return _.pick(item, _.without(propsToDehydrate, 'yAxisConfig', 'yAxisMin', 'yAxisMax')) as T;
  }
  return _.pick(item, propsToDehydrate as string[]) as T;
}
